import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Camera,
  Sparkles,
  Tag,
  ShieldCheck,
  MapPin,
  MessageSquare,
  Video,
  Search,
  Gavel,
} from "lucide-react";
import { fillSurface, useSurfaceWords } from "@/lib/surface";
import { useExpertFeatures } from "@/hooks/useExpertFeatures";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";
import { localizedPath } from "@/lib/i18n/slug-map";

export const Route = createFileRoute("/om")({
  head: () => ({
    meta: [
      { title: "Om Sellavo – fota eller filma, vi skriver annonsen" },
      {
        name: "description",
        content:
          "Så fungerar Sellavo: fota eller filma dina prylar, vi skriver annonstexten och föreslår pris. Sälj till fast pris med trygg betalning och frakt.",
      },
      { property: "og:title", content: "Om Sellavo – fota eller filma, vi skriver annonsen" },
      {
        property: "og:description",
        content:
          "Fota eller filma prylen, få färdig text och prisförslag och lägg ut annonsen. Så funkar Sellavo.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sellavo.com/om" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/om" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "Om Sellavo",
          url: "https://sellavo.com/om",
          description:
            "Så fungerar Sellavo: fota eller filma prylen, vi skriver annonstexten och föreslår pris.",
          mainEntity: {
            "@type": "Organization",
            name: "Sellavo",
            description: "Svensk marknadsplats där vi skriver annonsen åt dig.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: "https://sellavo.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Om Sellavo",
              item: "https://sellavo.com/om",
            },
          ],
        }),
      },
    ],
  }),

  component: OmPage,
});

const punkter = [
  {
    icon: Camera,
    title: "Fota prylarna",
    text: "Ta en bild – eller flera prylar på en gång. Vi håller isär dem och fixar bilderna.",
  },
  {
    icon: Video,
    title: "Videoskanning",
    text: "Filma en runda hemma, så plockar vi ut prylarna ur filmen och listar dem åt dig.",
  },
  {
    icon: Search,
    title: "Djupskanning",
    text: "För svårvärderade prylar gör vi en djupare koll och ger ett underbyggt prisintervall.",
  },
  {
    icon: Sparkles,
    title: "Färdig text",
    text: "Rubrik, beskrivning och kategori skrivs åt dig – ändra fritt innan du publicerar.",
  },
  {
    icon: Tag,
    title: "Prisförslag",
    text: "Ett realistiskt pris i kronor att utgå från, så du slipper gissa.",
  },
  {
    icon: Gavel,
    title: "Fast pris och bud",
    text: "Sätt ditt pris och välj själv om du vill ta emot bud från intresserade köpare.",
  },
  {
    icon: ShieldCheck,
    title: "Trygg affär",
    text: "Betalning och frakt kan skötas via sajten – pengarna släpps först när prylen kommit fram.",
  },
  {
    icon: MapPin,
    title: "Ungefärlig plats",
    text: "Kartan visar området prylen finns i – aldrig din exakta adress.",
  },
  {
    icon: MessageSquare,
    title: "Trygg kontakt",
    text: "Köpare hör av sig via inkorgen {på}. Din mejl och ditt telefonnummer visas aldrig.",
  },
];

export function OmPage() {
  const words = useSurfaceWords();
  const { enabled: testFeatures } = useExpertFeatures();
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).about;
  const translatedPoints = copy
    ? punkter.map((point, index) => ({ ...point, title: copy.points[index][0], text: copy.points[index][1] }))
    : punkter;
  const visiblePoints = testFeatures ? translatedPoints : translatedPoints.filter((_, index) => index !== 2);
  return (
    <main className="bg-background">
      <section className="bg-ocean-900 px-4 py-10 text-white sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-ocean-300">
            {copy?.eyebrow ?? "Det här är Sellavo"}
          </p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {copy?.title ?? "Om Sellavo – enklare att sälja dina prylar"}
          </h1>

          <p className="mt-3 text-[15px] leading-relaxed text-ocean-100 sm:mt-5 sm:text-lg">
            {copy?.intro ?? `${testFeatures ? "Fota, filma eller djupskanna" : "Fota eller filma"} dina prylar. Vi skriver annonstexten, föreslår kategori och sätter ett realistiskt pris – du behöver bara godkänna och publicera.`}
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
        <h2 className="font-heading text-xl font-bold sm:text-2xl">{copy?.how ?? "Så funkar det"}</h2>
        <div className="mt-5 grid gap-3 sm:mt-6 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
          {visiblePoints.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-md border border-border bg-card p-4 sm:p-5">
              <Icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-heading text-base font-semibold">{title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{fillSurface(text, words)}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          {copy?.detail ?? "Vill du se stegen mer i detalj?"}{" "}
          <Link to={localizedPath("/sa-fungerar-det", lang)} className="font-semibold text-primary hover:underline">
            {copy?.detailLink ?? "Så fungerar det"}
          </Link>{" "}
          {copy?.orRead ?? "eller läs om"}{" "}
          <Link to={localizedPath("/videoskanning", lang)} className="font-semibold text-primary hover:underline">
            {copy?.video ?? "videoskanning"}
          </Link>
          .
        </p>

        <h2 className="mt-12 font-heading text-xl font-bold sm:mt-14 sm:text-2xl">
          {copy?.whyTitle ?? "Varför vi byggde det"}
        </h2>
        <p className="mt-3 text-muted-foreground">
          {copy?.why ?? "Det mesta som ligger oanvänt hemma blir kvar där för att det tar för lång tid att lägga ut det: rubrik, beskrivning, kategori, skick och ett pris man ändå är osäker på. Vi ville göra vägen från ”den här kan jag sälja” till en publicerad annons så kort som möjligt – och lika enkel för tio prylar som för en."}
        </p>

        <h2 className="mt-12 font-heading text-xl font-bold sm:mt-14 sm:text-2xl">{copy?.costTitle ?? "Vad kostar det"}</h2>
        <p className="mt-3 text-muted-foreground">
          {copy?.cost ?? "Du kommer igång gratis och kan lägga ut annonser utan att betala något. Vill du lägga ut fler annonser per månad, använda videoskanning eller djupskanna svårvärderade prylar finns det på våra högre nivåer."}{" "}
          <Link to={localizedPath("/priser", lang)} className="font-semibold text-primary hover:underline">
            {copy?.costLink ?? "Se vad som ingår"}
          </Link>
          .
        </p>

        <h2 className="mt-12 font-heading text-xl font-bold sm:mt-14 sm:text-2xl">{copy?.privacyTitle ?? "Integritet"}</h2>
        <p className="mt-3 text-muted-foreground">
          {copy?.privacy ?? "Din e-post och ditt telefonnummer visas aldrig publikt – bara ditt visningsnamn syns i annonsen, och köpare kontaktar dig via meddelanden på sajten. Kartan förskjuts alltid något så att den visar ungefär vilket område prylen finns i, inte den exakta adressen. Vad som får läggas ut hittar du i"}{" "}
          <Link to={localizedPath("/regler", lang)} className="font-semibold text-primary hover:underline">
            {copy?.rules ?? "annonsreglerna"}
          </Link>
          .
        </p>

        <div className="mt-10 flex flex-col gap-3 sm:mt-12 sm:flex-row sm:flex-wrap">
          <Link
            to={localizedPath("/skapa", lang)}
            className="rounded-md bg-primary px-5 py-3 text-center font-heading font-semibold text-primary-foreground hover:bg-primary/90"
          >
            {copy?.create ?? "Lägg ut en annons"}
          </Link>
          <Link
            to={localizedPath("/annonser", lang)}
            className="rounded-md border border-input px-5 py-3 text-center font-medium hover:bg-accent"
          >
            {copy?.browse ?? "Se alla annonser"}
          </Link>
        </div>
      </section>
    </main>
  );
}
